import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Target, Loader2, RefreshCw, AlertTriangle, CheckCircle2, ShieldAlert,
  TrendingDown, FileDown, PlayCircle, Inbox, ListChecks, ChevronDown, ChevronUp,
} from 'lucide-react';

const API = '/api';

const BUCKET_STYLE = {
  A: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  B: 'bg-blue-50 text-blue-800 border-blue-200',
  C: 'bg-amber-50 text-amber-800 border-amber-200',
  D: 'bg-rose-50 text-rose-800 border-rose-200',
};

const SEVERITY_STYLE = {
  ok: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  warning: 'bg-amber-50 text-amber-800 border-amber-200',
  blocked: 'bg-rose-50 text-rose-800 border-rose-200',
};

function Section({ title, icon: Icon, count, children, defaultOpen = true, dataTestid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid={dataTestid}>
      <button onClick={() => setOpen(!open)} className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-muted/30">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-primary" />}
          <span className="text-sm font-semibold">{title}</span>
          {count != null && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{count}</span>}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && <div className="px-4 py-3 border-t border-border">{children}</div>}
    </div>
  );
}

export default function AdsV2Tab({ token }) {
  const [audit, setAudit] = useState(null);
  const [bottle, setBottle] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'insert' | 'monitor' | 'report'

  const authH = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, p] = await Promise.all([
        axios.get(`${API}/admin/ads-v2/audit`, { headers: authH }),
        axios.get(`${API}/admin/ads-v2/bottlenecks?hours=168`, { headers: authH }),
        axios.get(`${API}/admin/ads-v2/draft-plan`, { headers: authH }),
      ]);
      setAudit(a.data);
      setBottle(b.data);
      setPlan(p.data);
    } catch (e) {
      toast.error('Kon Ads V2 data niet laden');
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const insertIntoQueue = async (replace = false) => {
    if (!window.confirm(replace
      ? 'Vervang ALLE bestaande pending Ads V2 items met dit nieuwe plan? Geen Google Ads call.'
      : 'Voeg het Ads V2 plan toe als pending approval items? Geen Google Ads call.')) return;
    setBusy('insert');
    try {
      const { data } = await axios.post(
        `${API}/admin/ads-v2/insert-into-approval-queue${replace ? '?replace=true' : ''}`,
        {},
        { headers: authH },
      );
      if (data.success) {
        toast.success(`Ads V2: ${data.inserted} items in approval queue. Geen externe call.`);
        load();
      } else {
        toast.warning(data.reason || 'Items niet ingevoegd.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Insert mislukt');
    }
    setBusy(null);
  };

  const runMonitor = async () => {
    setBusy('monitor');
    try {
      const { data } = await axios.post(
        `${API}/admin/ads-v2/bottleneck-monitor/run?hours=24`,
        {},
        { headers: authH },
      );
      toast.success(`Monitor: ${data.diagnoses_count} diagnoses, ${data.todos_created} to-do's aangemaakt.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Monitor mislukt');
    }
    setBusy(null);
  };

  const downloadReport = async () => {
    setBusy('report');
    try {
      const res = await axios.get(`${API}/admin/ads-v2/report.md?hours=168`, {
        headers: authH,
        responseType: 'text',
      });
      const blob = new Blob([res.data], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `powersure_ads_v2_report_${new Date().toISOString().slice(0,10)}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Rapport gedownload');
    } catch (e) {
      toast.error('Download mislukt');
    }
    setBusy(null);
  };

  if (loading && !audit) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="ads-v2-loading">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  if (!audit) {
    return <div className="text-center py-8 text-sm text-muted-foreground">Geen data.</div>;
  }

  const api = audit.google_ads_api;
  const bucketCounts = audit.search_terms?.bucket_counts || {};
  const totalTerms = Object.values(bucketCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-4" data-testid="ads-v2-tab">
      {/* Header + safety banner */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-base sm:text-lg font-medium flex items-center gap-2">
          <Target className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          Ads V2 — Rigorous Rebuild
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium" data-testid="ads-v2-tag">
            {plan?.tag || 'ADS_V2'}
          </span>
        </h2>
        <button onClick={load} disabled={loading} className="px-2.5 py-1.5 text-xs border border-border rounded-md hover:bg-muted flex items-center gap-1.5">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-[11px] sm:text-xs text-blue-900" data-testid="ads-v2-safety">
        <strong>Veiligheid:</strong> deze tab is read-only over Google Ads. Approve/insert schrijft alleen naar `ads_agent_approval_queue` (intern). Geen budget, geen ad-publish, geen klantfunnel, geen Stripe/Daisycon/provider/DIA — `GOOGLE_ADS_MUTATION_ENABLED={String(audit.mutation_status?.google_ads_mutation_enabled)}`.
      </div>

      {/* Action strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="ads-v2-actions">
        <button
          onClick={() => insertIntoQueue(false)}
          disabled={busy === 'insert'}
          className="px-3 py-2.5 bg-primary text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="ads-v2-insert-btn"
        >
          {busy === 'insert' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Inbox className="w-4 h-4" />}
          Voeg V2 plan toe aan queue
        </button>
        <button
          onClick={runMonitor}
          disabled={busy === 'monitor'}
          className="px-3 py-2.5 border border-border rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-muted"
          data-testid="ads-v2-monitor-btn"
        >
          {busy === 'monitor' ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
          Run bottleneck monitor
        </button>
        <button
          onClick={downloadReport}
          disabled={busy === 'report'}
          className="px-3 py-2.5 border border-border rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-muted"
          data-testid="ads-v2-report-btn"
        >
          {busy === 'report' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
          Download rapport (.md)
        </button>
      </div>

      {/* Google Ads API status */}
      <Section title="Google Ads API status" icon={ShieldAlert} dataTestid="ads-v2-api-status">
        <div className="space-y-1.5 text-xs">
          <p>API geconfigureerd: <strong>{api.configured ? 'JA' : 'NEE'}</strong></p>
          <p>Live data: <strong className={api.live_data ? 'text-emerald-700' : 'text-amber-700'}>{api.live_data ? 'JA' : 'NEE (gebruik DB-cache)'}</strong></p>
          {api.missing_env?.length > 0 && (
            <p>Missende env vars: <code className="bg-muted px-1 rounded">{api.missing_env.join(', ')}</code></p>
          )}
          <p>Mutation flag: <strong className={audit.mutation_status?.google_ads_mutation_enabled ? 'text-rose-700' : 'text-emerald-700'}>
            {audit.mutation_status?.google_ads_mutation_enabled ? 'ENABLED (RISICO!)' : 'DISABLED (veilig)'}
          </strong></p>
          <p className="text-muted-foreground italic">{audit.mutation_status?.note}</p>
        </div>
      </Section>

      {/* Bottleneck diagnostics */}
      <Section title="Funnel bottleneck analyse (laatste 7 dagen)" icon={TrendingDown} count={bottle?.diagnoses_count} dataTestid="ads-v2-bottlenecks">
        {bottle?.events && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
            {Object.entries(bottle.events).map(([k, v]) => (
              <div key={k} className="bg-muted/40 rounded p-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, ' ')}</div>
                <div className="text-lg font-medium">{v}</div>
              </div>
            ))}
          </div>
        )}
        {bottle?.conversion_rates_pct && (
          <div className="bg-muted/20 rounded p-2 mb-3 text-[11px] sm:text-xs space-y-1">
            {Object.entries(bottle.conversion_rates_pct).map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-muted-foreground">{k.replace(/_/g, ' ')}:</span>
                <span className="font-medium">{v}%</span>
              </div>
            ))}
          </div>
        )}
        {bottle?.diagnoses?.length > 0 ? (
          <div className="space-y-1.5" data-testid="ads-v2-diagnoses-list">
            {bottle.diagnoses.map((d, i) => (
              <div key={i} className={`border rounded-md px-2.5 py-2 text-xs ${SEVERITY_STYLE[d.severity] || SEVERITY_STYLE.warning}`} data-testid={`ads-v2-diagnosis-${d.rule}`}>
                <div className="flex items-center gap-1.5 font-semibold">
                  {d.severity === 'blocked' ? <AlertTriangle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                  {d.headline}
                  <span className="text-[9px] uppercase tracking-wider opacity-70">{d.rule}</span>
                </div>
                <p className="mt-0.5">{d.detail}</p>
                <p className="mt-0.5 italic opacity-80">Suggestie: {d.suggestion}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-emerald-700 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Geen actieve bottlenecks gevonden in dit venster.
          </p>
        )}
      </Section>

      {/* Search term quality */}
      <Section title="Search-term kwaliteit (A/B/C/D)" icon={ListChecks} count={totalTerms} defaultOpen={false} dataTestid="ads-v2-search-terms">
        <div className="grid grid-cols-4 gap-2 mb-3" data-testid="ads-v2-bucket-summary">
          {(['A','B','C','D']).map((b) => (
            <div key={b} className={`text-center rounded-md p-2 border ${BUCKET_STYLE[b]}`} data-testid={`ads-v2-bucket-${b}`}>
              <div className="text-[10px] uppercase tracking-wider font-semibold">{b}</div>
              <div className="text-lg font-medium">{bucketCounts[b] || 0}</div>
              <div className="text-[10px]">{ {A:'High',B:'Mid',C:'Low',D:'Risk'}[b] }</div>
            </div>
          ))}
        </div>
        {audit.search_terms?.rows?.length > 0 ? (
          <div className="max-h-64 overflow-y-auto border border-border rounded text-xs">
            <table className="w-full">
              <thead className="bg-muted/40 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1">Term</th>
                  <th className="text-left px-2 py-1">Bucket</th>
                  <th className="text-left px-2 py-1 hidden sm:table-cell">Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {audit.search_terms.rows.slice(0, 100).map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-2 py-1 truncate max-w-[200px]">{r.search_term}</td>
                    <td className="px-2 py-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${BUCKET_STYLE[r.bucket]}`}>{r.bucket}</span>
                    </td>
                    <td className="px-2 py-1 text-[10px] text-muted-foreground hidden sm:table-cell">{r.recommendation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Geen search terms beschikbaar.</p>
        )}
      </Section>

      {/* Ads V2 draft plan */}
      <Section title={`Ads V2 draft plan (${plan?.stats?.ad_groups || 0} ad groups)`} icon={Target} dataTestid="ads-v2-plan">
        <div className="text-xs text-muted-foreground mb-2">
          Campagne: <code className="bg-muted px-1 rounded">{plan?.plan?.campaign?.name}</code> · Bidding: {plan?.plan?.campaign?.bidding_strategy} · Niet inschakelen: {plan?.plan?.campaign?.exclude?.join(', ')}
        </div>
        <div className="space-y-2">
          {plan?.plan?.ad_groups?.map((ag) => (
            <details key={ag.key} className="border border-border rounded-md" data-testid={`ads-v2-plan-${ag.key}`}>
              <summary className="px-3 py-2 cursor-pointer flex items-center justify-between text-sm font-medium hover:bg-muted/30">
                <span>{ag.name}</span>
                <span className="text-[10px] text-muted-foreground">{ag.keywords.length} kw · {ag.rsa.headlines.length} H · {ag.rsa.descriptions.length} D</span>
              </summary>
              <div className="px-3 py-2 border-t border-border text-xs space-y-2">
                <p className="text-muted-foreground italic">Intent: {ag.intent}</p>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Keywords ({ag.keywords.length})</div>
                  <div className="flex flex-wrap gap-1">
                    {ag.keywords.map((k, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-muted border border-border">
                        <code>{k.text}</code> <span className="opacity-50">[{k.match}]</span>
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">RSA Headlines ({ag.rsa.headlines.length})</div>
                  <ul className="text-[11px] space-y-0.5 pl-3 list-disc">
                    {ag.rsa.headlines.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">RSA Descriptions ({ag.rsa.descriptions.length})</div>
                  <ul className="text-[11px] space-y-0.5 pl-3 list-disc">
                    {ag.rsa.descriptions.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Final URL</div>
                  <code className="text-[10px] break-all bg-muted p-1 block rounded">{ag.final_url}</code>
                </div>
              </div>
            </details>
          ))}
        </div>
      </Section>

      {/* Negatives */}
      <Section title={`Negative keyword draft (${plan?.plan?.negatives_campaign_level?.length || 0})`} icon={ShieldAlert} defaultOpen={false} dataTestid="ads-v2-negatives">
        <div className="max-h-64 overflow-y-auto border border-border rounded text-xs">
          <table className="w-full">
            <thead className="bg-muted/40 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1">Term</th>
                <th className="text-left px-2 py-1">Match</th>
                <th className="text-left px-2 py-1 hidden sm:table-cell">Reden</th>
              </tr>
            </thead>
            <tbody>
              {plan?.plan?.negatives_campaign_level?.map((n, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-2 py-1"><code>{n.text}</code></td>
                  <td className="px-2 py-1 text-[10px]">{n.match}</td>
                  <td className="px-2 py-1 text-[10px] text-muted-foreground hidden sm:table-cell">{n.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Approval queue summary */}
      <Section title="Approval Queue" icon={Inbox} dataTestid="ads-v2-queue">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="bg-muted/40 rounded p-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pending totaal</div>
            <div className="text-lg font-medium">{audit.approval_queue?.pending_total || 0}</div>
          </div>
          <div className="bg-primary/5 border border-primary/20 rounded p-2">
            <div className="text-[10px] uppercase tracking-wider text-primary">Ads V2 pending</div>
            <div className="text-lg font-medium">{audit.approval_queue?.ads_v2_existing || 0}</div>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2 italic">
          Items zijn pending tot je ze handmatig approve via Ads Automation Agent tab. Approve = nog steeds geen Google Ads call zolang MUTATION_ENABLED=false.
        </p>
      </Section>
    </div>
  );
}
